<template>
  <div class="balance-box rounded flex flex-col">
    <div v-if="props.showTitle">{{ title }}</div>

    <div class="flex-1 min-h-0">
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center"
      >
        <Spin size="1em"></Spin>
      </div>

      <div class="w-full h-full" v-else-if="data.length > 0">
        <VChart class="chart" :option="option" style="height: 184px"></VChart>
      </div>
      <div
        v-if="data.length == 0"
        class="w-full h-full flex items-center text-xs text-white-070 mt-4"
      >
        {{ text || '' }}
        <div v-if="!text" class="flex items-center justify-center">
          <img
            src="@/assets/bgs/no_data.avif"
            alt="no daos data found"
            class="relative"
          />
          <div class="text-white-040 absolute">No Data</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import dayjs from 'dayjs';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ScatterChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
} from 'echarts/components';
import VChart from 'vue-echarts';
import { computed, defineProps } from 'vue';
import Spin from '@/components/Spin.vue';

use([
  CanvasRenderer,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
]);

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: { type: Boolean, default: true },
  cutPoint: Number,
  showTitle: {
    type: Boolean,
    default: false,
  },
  title: { type: String, default: 'valuation by history' },
  formatter: {
    type: Function,
    default: ({ componentType, data }) => {
      if (componentType == 'markLine') {
        return '';
      }
      return `${data[1]} ETH <br/> ${dayjs(data[0]).format(
        'MMM DD YYYY  hh:mmA',
      )} `;
    },
  },
  text: {
    type: String,
    default: 'No Data',
  },
});

const data = computed(() => {
  return props.data;
});

/* 图表数据的完整性还有待调整 */
const option = computed(() => {
  const series = {
    type: 'scatter',
    data: data.value.map((x) => [x.tradeTime, +x.historyPrice]),
    symbolSize: 8,
    itemStyle: {
      color: '#FF558566',
      borderColor: '#FF5585FF',
    },
  };

  if (props.cutPoint) {
    series.markLine = {
      silent: true,
      symbol: 'none',
      label: {
        color: '#fff',
        position: 'end',
        formatter: 'Cut Time',
      },
      lineStyle: {
        type: 'solid',
        color: '#fff',
      },
      data: [
        {
          name: 'Cut Time',
          xAxis: props.cutPoint,
        },
      ],
    };
  }

  return {
    grid: {
      top: '30',
      left: '50',
      right: '30',
      bottom: '20',
    },
    xAxis: {
      // show: false,

      axisLine: {
        lineStyle: {
          color: '#fff',
        },
      },

      splitLine: {
        show: false,
      },
      axisLabel: {
        color: '#ffffff4d',
        formatter: (value) => dayjs(value).format('MMM DD'),
      },
      min:
        data.value.length - 1 < 0
          ? 0
          : data.value[data.value.length - 1].tradeTime,
      max: data.value[0].tradeTime,
    },
    yAxis: {
      axisLabel: {
        color: '#ffffff4d',
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: '#fff',
        },
      },
      // show: false,
    },
    tooltip: {
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#FFFFFF66',
          type: 'solid',
        },
        label: {
          show: false,
          formatter: ({ axisDimension, value }) => {
            if (axisDimension == 'x') {
              return '';
            }
            return value;
          },
        },
      },
      borderColor: '#FFFFFF66',
      backgroundColor: '#1e1f21',
      textStyle: {
        color: '#fff',
      },
      formatter: props.formatter,
    },
    color: '#FF5585FF',
    series: [series],
  };
});
</script>

<style scoped>
.chart {
  width: 100%;
  height: 100%;
}
</style>
